import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Pagination,
  Skeleton,
} from '@ntconsult/aluma-react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmptyScope } from '../hooks/useEmptyScope';
import { useExportPool } from '../hooks/useExportPool';
import { usePaginationLabels } from '../hooks/usePaginationLabels';
import { usePoolAllocations } from '../hooks/usePoolAllocations';
import { POOL_COLUMN_LABEL_KEYS, usePoolColumns } from '../hooks/usePoolColumns';
import { usePoolFilters } from '../hooks/usePoolFilters';
import { formatDisplayDate } from '../utils/data';
import { i18nKeyForError, isSessionError, parseApiError } from '../utils/errors';
import { AllocationModal, type EditAllocationTarget } from './AllocationModal';
import { CheckToolbar } from './CheckToolbar';
import { ColumnsPicker } from './ColumnsPicker';
import { ExportButton } from './ExportButton';
import { NoIdentityState } from './NoIdentityState';
import { PerPageSelect } from './PerPageSelect';
import { PoolFilterChips } from './PoolFilterChips';
import { PoolFiltersDrawer } from './PoolFiltersDrawer';
import { PoolTable } from './PoolTable';
import { PINNED_BLOCK, WIDE_PAGE } from './wideTable';

type ModalTarget = { type: 'new' } | { type: 'edit'; target: EditAllocationTarget } | null;

export function PoolPage() {
  const { t, i18n } = useTranslation('alocacao');
  const paginationLabels = usePaginationLabels();
  const filterState = usePoolFilters();
  const query = usePoolAllocations(filterState.filters);
  const columnsState = usePoolColumns();
  const exportPool = useExportPool(filterState.filters, columnsState.visibleColumns);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState<ModalTarget>(null);
  const scope = useEmptyScope();

  const { data, isLoading, isError } = query;
  const error = parseApiError(query.error);
  const isListLoading = isLoading || query.isPlaceholderData;

  if (isError && error && isSessionError(error)) return <NoIdentityState code={error.code} />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;
  // Sem filtro e sem ninguém, o estado vazio substitui a tabela.
  const nobodyInPool = data && data.total === 0 && !filterState.hasActiveFilters;

  return (
    <div className={`flex flex-col gap-6 ${WIDE_PAGE}`}>
      <div className={PINNED_BLOCK}>
        <PageHeader
          title={t('consultas.pool.titulo')}
          description={t('consultas.pool.descricao')}
          actions={
            !scope.loading &&
            !scope.empty && (
              <Button
                variant="primary"
                leftIcon={<Plus size={16} />}
                onClick={() => setModalTarget({ type: 'new' })}
              >
                {t('nova')}
              </Button>
            )
          }
        />
      </div>

      <section className="flex min-w-0 flex-col gap-4" aria-label={t('consultas.pool.titulo')}>
        <div className={PINNED_BLOCK}>
          <CheckToolbar
            date={filterState.date}
            appliedDate={data?.date}
            onDateChange={filterState.setDate}
            dateHint={t('consultas.pool.dataReferenciaDica')}
            totalLabel={
              isListLoading || !data ? undefined : t('consultas.total', { count: data.total })
            }
            onOpenFilters={() => setAdvancedOpen(true)}
            actions={
              <>
                <ColumnsPicker columnsState={columnsState} labelKeys={POOL_COLUMN_LABEL_KEYS} />
                <ExportButton
                  exportMutation={exportPool}
                  disabled={!data?.total}
                  labels={{
                    title: t('consultas.pool.exportarTitulo'),
                    allPages: t('exportacao.todasPaginasRegistros', { count: data?.total ?? 0 }),
                    currentPage: t('exportacao.paginaAtualRegistros', {
                      count: data?.items.length ?? 0,
                    }),
                  }}
                />
              </>
            }
          />
        </div>

        <div className={PINNED_BLOCK}>
          <PoolFilterChips filterState={filterState} filterOptions={data?.filterOptions} />
        </div>

        {isListLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {isError && (
          <Alert
            variant="danger"
            title={t((error ? i18nKeyForError(error.code) : 'erros.unknown') as never)}
          />
        )}

        {!isListLoading && !isError && nobodyInPool && (
          <EmptyState
            title={t('consultas.pool.vazioTitulo', {
              date: formatDisplayDate(data.date, i18n.language),
            })}
            description={t('consultas.pool.vazioDescricao')}
          />
        )}

        {!isListLoading && !isError && data && !nobodyInPool && (
          <div className="flex flex-col gap-4">
            <PoolTable
              items={data.items}
              filterOptions={data.filterOptions}
              filterState={filterState}
              columnsState={columnsState}
              referenceDate={data.date}
              onEdit={(target) => setModalTarget({ type: 'edit', target })}
            />
            <div className={`flex flex-wrap items-center gap-4 ${PINNED_BLOCK}`}>
              <PerPageSelect value={filterState.perPage} onChange={filterState.setPerPage} />
              {totalPages > 1 && (
                <Pagination
                  {...paginationLabels}
                  page={filterState.filters.page}
                  totalPages={totalPages}
                  onPageChange={filterState.goToPage}
                />
              )}
            </div>
          </div>
        )}
      </section>

      <PoolFiltersDrawer
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filterState={filterState}
        filterOptions={data?.filterOptions}
      />

      {data && (
        <AllocationModal
          open={modalTarget !== null}
          onClose={() => setModalTarget(null)}
          target={modalTarget?.type === 'edit' ? modalTarget.target : null}
          today={data.date}
        />
      )}
    </div>
  );
}
